/**
 * Did the projection happen?
 *
 * <h2>Why this can exist at all</h2>
 * Only because the count is anchored. A reading taken at the last bar projects into bars that do
 * not exist, so there is nothing to check it against and never will be until time passes. A reading
 * taken at bar 4,001 of 8,399 projects into 4,398 bars that already happened — so every target it
 * casts is a claim with an answer, and the answer is on the same chart.
 *
 * <h2>The four ways a projection ends</h2>
 * <ul>
 *   <li>REACHED — price traded through the target.</li>
 *   <li>INVALIDATED — price traded back through the pivot the projection was measured FROM, before
 *       reaching the target. That pivot is the count's own stop in all three cases this module
 *       projects: a wave 3 from the end of wave 2 is broken if price re-enters wave 2, a wave 5
 *       from the end of wave 4 if price re-enters wave 4, a wave C from the end of wave B if price
 *       re-enters B. One uniform rule, and it is the chartist's rule rather than a convenience.</li>
 *   <li>EXPIRED — the horizon ran out and neither level was touched. A failure, but a different one
 *       from a broken count, and measured on SPY they are of comparable size — so they are counted
 *       together against the hit rate and reported apart.</li>
 *   <li>OPEN — the CHART ran out before the horizon did. The only genuinely unresolved case, and
 *       the only one excluded from the hit rate.</li>
 * </ul>
 *
 * <h2>The horizon is not optional</h2>
 * Without one, "was the target reached" is nearly always yes on an instrument that rises over
 * decades: give a 1.618 projection four thousand bars and price will wander through it eventually,
 * having been invalidated and re-formed a hundred times on the way. That answer would be true and
 * worthless. A projection is a claim about the wave in progress, so it is judged over something
 * like the time that wave should take — {@link HORIZON_LEG_MULTIPLE} times the measured leg's own
 * bar length, floored at {@link MIN_HORIZON_BARS} so a short leg still gets a fair test.
 *
 * <h2>Intrabar order is unknowable, and is not guessed</h2>
 * A single daily bar whose range covers both the target and the invalidation could have touched
 * either first; the OHLC does not say. Those are marked `ambiguous` and counted as INVALIDATED,
 * because the alternative — resolving them favourably — inflates the one number this whole module
 * exists to report.
 */

/** Bars per unit of the measured leg's length that a projection is given to resolve. */
export const HORIZON_LEG_MULTIPLE = 3;

/** No projection is judged over less than this, however short the leg it came from. */
export const MIN_HORIZON_BARS = 20;

export const VERDICT = {
  /** Price traded through the target. */
  REACHED: 'REACHED',
  /** Price traded back through the pivot the projection was measured from. The count broke. */
  INVALIDATED: 'INVALIDATED',
  /**
   * The horizon ran out with neither level touched.
   *
   * <p>Separate from INVALIDATED although both count against the hit rate, because they are
   * different failures and lumping them hides which one a reader is looking at: a count that broke
   * was WRONG, while one that expired may have been right and slow. On SPY the two are of
   * comparable size, so the distinction is not hypothetical.
   */
  EXPIRED: 'EXPIRED',
  /** The chart ended before the horizon did. Genuinely unresolved. */
  OPEN: 'OPEN',
  /** Nothing to test against - the anchor is the last bar. */
  UNTESTABLE: 'UNTESTABLE',
};

/** Bars a projection measured against a `legBars`-long wave is given to resolve. */
export function horizonFor(legBars) {
  const bars = Number.isFinite(legBars) ? Math.abs(legBars) : 0;
  return Math.max(MIN_HORIZON_BARS, Math.round(bars * HORIZON_LEG_MULTIPLE));
}

/**
 * One projection, walked forward through the bars that followed the anchor.
 *
 * @param {object} projection from `allProjections`
 * @param {Array<{high:number,low:number}|null>} bars index-aligned to the chart (see
 *        `toVerificationBars`) — nulls are skipped, keeping every index meaningful
 * @param {{anchorIndex:number}} opts
 * @returns {object} the projection with its verdict and what it did on the way
 */
export function verifyProjection(projection, bars, { anchorIndex } = {}) {
  const up = projection?.direction >= 0;
  const target = projection?.price;
  const base = projection?.basePrice;
  const horizon = horizonFor(projection?.legBars);

  const blank = extra => ({
    ...projection,
    verdict: VERDICT.UNTESTABLE,
    horizonBars: horizon,
    barsTested: 0,
    barsToResolve: null,
    reachedAtIndex: null,
    // The closest price came to the target, as a share of the distance it had to travel. Reported
    // for every verdict, because "missed by 3%" and "never went that way at all" are different
    // failures and a bare NOT REACHED hides which one happened.
    bestFraction: null,
    ambiguous: false,
    ...extra,
  });

  if (!Number.isFinite(target) || !Number.isFinite(base) || !Number.isInteger(anchorIndex)) {
    return blank();
  }
  if (!Array.isArray(bars) || anchorIndex + 1 >= bars.length) {
    // The anchor is the last bar, or beyond it. Nothing has happened yet — which is a fact about
    // the chart, not a failure of the projection, and must not be counted as one.
    return blank();
  }

  const span = target - base;
  if (span === 0) return blank();

  const last = Math.min(bars.length - 1, anchorIndex + horizon);
  let best = 0;
  let tested = 0;

  for (let i = anchorIndex + 1; i <= last; i++) {
    const bar = bars[i];
    if (!bar) continue;
    tested++;

    const reached = up ? bar.high >= target : bar.low <= target;
    const broken = up ? bar.low <= base : bar.high >= base;

    // Favourable excursion, measured on the same side the projection points.
    const far = up ? bar.high : bar.low;
    best = Math.max(best, (far - base) / span);

    if (reached && broken) {
      // Both inside one bar's range. Which came first is not in the data, so it is not decided
      // here — it is recorded and counted the conservative way.
      return blank({
        verdict: VERDICT.INVALIDATED,
        ambiguous: true,
        barsTested: tested,
        barsToResolve: i - anchorIndex,
        bestFraction: Math.min(1, Math.max(0, best)),
      });
    }
    if (reached) {
      return blank({
        verdict: VERDICT.REACHED,
        barsTested: tested,
        barsToResolve: i - anchorIndex,
        reachedAtIndex: i,
        bestFraction: Math.min(1, Math.max(0, best)),
      });
    }
    if (broken) {
      return blank({
        verdict: VERDICT.INVALIDATED,
        barsTested: tested,
        barsToResolve: i - anchorIndex,
        bestFraction: Math.min(1, Math.max(0, best)),
      });
    }
  }

  // Ran out of horizon, or ran out of chart. Only the second is genuinely unresolved: a projection
  // given its full horizon and reaching neither end HAS been tested, and answering "open" would let
  // every slow failure escape the tally.
  const ranOutOfChart = last < anchorIndex + horizon;
  return blank({
    verdict: ranOutOfChart ? VERDICT.OPEN : VERDICT.EXPIRED,
    barsTested: tested,
    bestFraction: Math.min(1, Math.max(0, best)),
  });
}

/** Every projection, verified. */
export function verifyProjections(projections, bars, opts) {
  return (Array.isArray(projections) ? projections : []).map(p => verifyProjection(p, bars, opts));
}

/** The median of a list of numbers, or null when it is empty. */
function median(values) {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * How many of the projections were meaningful.
 *
 * <p>The hit rate's denominator is `reached + invalidated` — the ones that RESOLVED. Counting the
 * still-open ones as misses would penalise a projection for the chart ending, which says nothing
 * about the projection; counting them as hits would be worse. It is the same distinction the Spiral
 * inspector draws between an unconfirmed rung and a missed one, and for the same reason.
 */
export function projectionSummary(verified) {
  const rows = Array.isArray(verified) ? verified : [];
  const by = v => rows.filter(r => r.verdict === v);

  const reached = by(VERDICT.REACHED);
  const invalidated = by(VERDICT.INVALIDATED);
  const expired = by(VERDICT.EXPIRED);
  // Both failures count against the rate. A projection that ran its full horizon without getting
  // there did not work, whatever the reason - excluding it would let every slow failure vanish.
  const failed = [...invalidated, ...expired];
  const resolved = reached.length + failed.length;

  return {
    total: rows.length,
    reached: reached.length,
    invalidated: invalidated.length,
    expired: expired.length,
    open: by(VERDICT.OPEN).length,
    untestable: by(VERDICT.UNTESTABLE).length,
    ambiguous: rows.filter(r => r.ambiguous).length,
    resolved,
    hitRate: resolved > 0 ? reached.length / resolved : null,
    medianBarsToReach: median(reached.map(r => r.barsToResolve).filter(Number.isFinite)),
    // Of the ones that failed, how far they got on average. A set of misses that all travelled 80%
    // of the way is a different reading from a set that never moved.
    medianMissFraction: median(failed.map(r => r.bestFraction).filter(Number.isFinite)),
  };
}

/** A percentage, or an em dash — never "NaN%" and never a bare 0 standing in for "unknown". */
function pct(value) {
  return Number.isFinite(value) ? `${Math.round(value * 100)}%` : '—';
}

/**
 * The summary as one line, for the status bar and the journal.
 *
 * <p>Leads with the resolved count rather than the total, because "4 of 6" invites the reader to
 * assume the other two were misses. They were not; they are named separately.
 */
export function projectionScoreline(summary) {
  if (!summary || summary.total === 0) return 'No standing projections at this bar.';
  if (summary.untestable === summary.total) {
    return `${summary.total} projection(s), none testable yet — no bars after the anchor.`;
  }

  const parts = [`${summary.reached} of ${summary.resolved} resolved projections reached `
    + `(${pct(summary.hitRate)})`];
  if (Number.isFinite(summary.medianBarsToReach)) {
    parts.push(`median ${summary.medianBarsToReach} bars to reach`);
  }
  if (summary.invalidated > 0) parts.push(`${summary.invalidated} broke the count`);
  if (summary.expired > 0) parts.push(`${summary.expired} ran out of time`);
  if (summary.invalidated + summary.expired > 0 && Number.isFinite(summary.medianMissFraction)) {
    parts.push(`the misses covered ${pct(summary.medianMissFraction)} of the way`);
  }
  if (summary.open > 0) parts.push(`${summary.open} still open`);
  if (summary.untestable > 0) parts.push(`${summary.untestable} untestable`);
  if (summary.ambiguous > 0) {
    parts.push(`${summary.ambiguous} resolved inside a single bar's range and were counted against`);
  }
  return `${parts.join(' · ')}.`;
}
